import http from './api'
import {
  HowIEatSpace,
  HowIEatProfile,
  HowIEatToday,
  HowIEatMeal,
  HowIEatMealItem,
  HowIEatWeightEntry,
  HowIEatFoodItem,
  HowIEatWeekProgress,
  HowIEatMealTemplate,
  HowIEatFastingStatus,
  HowIEatAchievement,
  MenuItemNutritionPreview,
  FoodPhotoAnalysisResult,
} from '../types/howIEat'

class HowIEatService {
  async getMySpace(): Promise<HowIEatSpace | null> {
    const response = await http.get<HowIEatSpace | null>('/how-i-eat/me')
    return response.data
  }

  async createSpace(): Promise<HowIEatSpace> {
    const response = await http.post<HowIEatSpace>('/how-i-eat')
    return response.data
  }

  async getSpace(id: string): Promise<HowIEatSpace> {
    const response = await http.get<HowIEatSpace>(`/how-i-eat/spaces/${id}`)
    return response.data
  }

  async getProfile(): Promise<HowIEatProfile> {
    const response = await http.get<HowIEatProfile>('/how-i-eat/profile')
    return response.data
  }

  async updateProfile(data: Partial<HowIEatProfile>): Promise<HowIEatProfile> {
    const response = await http.patch<HowIEatProfile>('/how-i-eat/profile', data)
    return response.data
  }

  async getToday(date?: string): Promise<HowIEatToday> {
    const response = await http.get<HowIEatToday>('/how-i-eat/today', { params: date ? { date } : {} })
    return response.data
  }

  async createMeal(data: {
    mealType: string
    items: HowIEatMealItem[]
    notes?: string
  }): Promise<HowIEatMeal> {
    const response = await http.post<HowIEatMeal>('/how-i-eat/meals', data)
    return response.data
  }

  async getMeal(mealId: string): Promise<HowIEatMeal> {
    const response = await http.get<HowIEatMeal>(`/how-i-eat/meals/${mealId}`)
    return response.data
  }

  async updateMeal(
    mealId: string,
    data: {
      mealType?: string
      items?: HowIEatMealItem[]
      notes?: string
    },
  ): Promise<HowIEatMeal> {
    const response = await http.patch<HowIEatMeal>(`/how-i-eat/meals/${mealId}`, data)
    return response.data
  }

  async deleteMeal(mealId: string): Promise<void> {
    await http.delete(`/how-i-eat/meals/${mealId}`)
  }

  async logWater(amountMl: number): Promise<{ totalMl: number }> {
    const response = await http.post<{ totalMl: number }>('/how-i-eat/water', { amountMl })
    return response.data
  }

  async logWeight(weightKg: number, notes?: string): Promise<void> {
    await http.post('/how-i-eat/weight', { weightKg, notes })
  }

  async getWeightHistory(days = 30): Promise<HowIEatWeightEntry[]> {
    const response = await http.get<HowIEatWeightEntry[]>('/how-i-eat/weight', { params: { days } })
    return response.data
  }

  async getWeekProgress(endDate?: string): Promise<HowIEatWeekProgress> {
    const response = await http.get<HowIEatWeekProgress>('/how-i-eat/progress/week', {
      params: endDate ? { endDate } : {},
    })
    return response.data
  }

  async searchFoods(q: string, limit = 20): Promise<HowIEatFoodItem[]> {
    const response = await http.get<HowIEatFoodItem[]>('/how-i-eat/foods/search', { params: { q, limit } })
    return response.data
  }

  async lookupBarcode(barcode: string): Promise<HowIEatFoodItem> {
    const code = barcode.replace(/\D/g, '')
    const response = await http.get<HowIEatFoodItem>(`/how-i-eat/foods/barcode/${code}`)
    return response.data
  }

  async createFood(data: {
    name: string
    brand?: string
    barcode?: string
    caloriesPer100g: number
    proteinPer100g: number
    carbsPer100g: number
    fatsPer100g: number
    defaultServingG?: number
  }): Promise<HowIEatFoodItem> {
    const response = await http.post<HowIEatFoodItem>('/how-i-eat/foods', data)
    return response.data
  }

  async getTemplates(): Promise<HowIEatMealTemplate[]> {
    const response = await http.get<HowIEatMealTemplate[]>('/how-i-eat/templates')
    return response.data
  }

  async createTemplate(data: {
    name: string
    mealType: string
    items: HowIEatMealItem[]
    notes?: string
  }): Promise<HowIEatMealTemplate> {
    const response = await http.post<HowIEatMealTemplate>('/how-i-eat/templates', data)
    return response.data
  }

  async deleteTemplate(templateId: string): Promise<void> {
    await http.delete(`/how-i-eat/templates/${templateId}`)
  }

  async useTemplate(templateId: string): Promise<HowIEatMeal> {
    const response = await http.post<HowIEatMeal>(`/how-i-eat/templates/${templateId}/use`)
    return response.data
  }

  async getFastingStatus(): Promise<HowIEatFastingStatus> {
    const response = await http.get<HowIEatFastingStatus>('/how-i-eat/fasting')
    return response.data
  }

  async startFasting(targetHours?: number): Promise<HowIEatFastingStatus> {
    const response = await http.post<HowIEatFastingStatus>('/how-i-eat/fasting/start', { targetHours })
    return response.data
  }

  async endFasting(): Promise<HowIEatFastingStatus> {
    const response = await http.post<HowIEatFastingStatus>('/how-i-eat/fasting/end')
    return response.data
  }

  async getFastingHistory(limit = 7) {
    const response = await http.get<import('../types/howIEat').HowIEatFastingHistoryEntry[]>(
      '/how-i-eat/fasting/history',
      { params: { limit } },
    )
    return response.data
  }

  async getAchievements(): Promise<HowIEatAchievement[]> {
    const response = await http.get<HowIEatAchievement[]>('/how-i-eat/achievements')
    return response.data
  }

  async previewMenuItem(menuItemId: string): Promise<MenuItemNutritionPreview> {
    const response = await http.get<MenuItemNutritionPreview>(`/how-i-eat/menu-items/${menuItemId}/preview`)
    return response.data
  }

  async createMealFromMenu(data: {
    menuItemId: string
    mealType: string
    calories?: number
    proteinG?: number
    carbsG?: number
    fatsG?: number
    notes?: string
  }): Promise<HowIEatMeal> {
    const response = await http.post<HowIEatMeal>('/how-i-eat/meals/from-menu', data)
    return response.data
  }

  async analyzeFoodPhoto(file: File | Blob, locale?: string): Promise<FoodPhotoAnalysisResult> {
    const formData = new FormData()
    formData.append('photo', file, file instanceof File ? file.name : 'meal.jpg')
    if (locale) formData.append('locale', locale)
    const response = await http.post<FoodPhotoAnalysisResult>('/how-i-eat/analyze-food-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    })
    return response.data
  }
}

const howIEatService = new HowIEatService()
export default howIEatService
